// AuthProvider.jsx — thin React binding over auth.js.
// Provides { user, signedIn, authorized, error, resuming } via useAuth().

import React from 'react';
import { getAuthState, initAuth, onAuthChange, sessionResumable } from './auth';

const AuthCtx = React.createContext({ ...getAuthState(), resuming: false });

export function AuthProvider({ children }) {
  const [state, setState] = React.useState(getAuthState);
  // Seeded synchronously during render (the predicate is pure — it reads
  // storage and module state and starts nothing). Raising it in the effect
  // instead would paint the sign-in screen first and swap to the splash one
  // commit later, unmounting and remounting everything underneath.
  const [resuming, setResuming] = React.useState(sessionResumable);

  React.useEffect(() => {
    const off = onAuthChange((s) => { setState(s); setResuming(false); });
    const resumed = initAuth();                 // idempotent (StrictMode-safe)
    // Re-read rather than trusting `resumed`: initAuth returns false on a
    // StrictMode second mount because it is idempotent, and lowering the flag
    // on that would drop the splash while the first mount's resume is in
    // flight.
    setResuming(resumed || sessionResumable());
    setState(getAuthState());
    return off;
  }, []);

  const value = React.useMemo(() => ({ ...state, resuming }), [state, resuming]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() { return React.useContext(AuthCtx); }
